import React, { useEffect, useRef, useState } from 'react';
import { ProgressCircle } from '@react-spectrum/progress';
import useCaps from "state/capsTab/hooks/useCaps";
import {IllustratedMessage, Content, Heading} from '@adobe/react-spectrum'
import Error from "@spectrum-icons/illustrations/Error";
import { getConfig } from 'config';
import './CapsUi.css';

const getBetaKeyParam = (isInternalUser) => {
  if (!isInternalUser) return {};

  const urlParams = new URLSearchParams(window.location.search);
  const betaKey = urlParams.get('betaKey');

  return betaKey &&
    betaKey.match(/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i) ?
    { betaKey } : {};
};

const CapsUi = () => {
  const {
    config,
    project,
    environment,
    capsUiUrl,
    isLoading,
    isInternalUser,
    fetchToken,
    imsLoginRequired,
    capsError,
    adobeEmail
  } = useCaps();
  const containerRef = useRef(null);
  const [error, setError] = useState(null);
  const capsElementRef = useRef(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);

  // Surface unexpected token/access errors through the existing error state
  useEffect(() => {
    if (capsError) {
      setError('Unable to load Patching Automation. Please reload the page to try again.');
    }
  }, [capsError]);

  // Element attributes
  const [attributes, setAttributes] = useState({
    theme: 'light',
    heading: ' ',
    project: project,
    environment: environment
  });

  // Update attributes when project and environment changes
  useEffect(() => {
    if (project && environment) {
      setAttributes(prev => ({
        ...prev,
        project: project,
        environment: environment
      }));
    }
  }, [project, environment]);

  const applyConfigAndAttributes = () => {
    if (!capsElementRef.current) return;

    try {
      // Set attributes
      Object.entries(attributes).forEach(([key, value]) => {
        capsElementRef.current.setAttribute(key, value);
      });

      // Set config
      capsElementRef.current.setConfig({
        endpoint: config.endpoint,
        token: config.token,
        persistEnvironmentSelection: false,
        condensedView: !isInternalUser,
        isInternalUser,
        imsLoginUrl: `${getConfig('apiDomain')}/adobe/login?redirect_uri=tab/patchingAutomation`,
        onTokenExpired: async () => {
          const response = await fetchToken();
          return response.data.token;
        },
        params: {
          ...getBetaKeyParam(!!isInternalUser),
        },
      });

      if (imsLoginRequired) {
        capsElementRef.current.setError('ims_session_required');
      }
    } catch (err) {
      setError('Failed to apply configuration: ' + err.message);
    }
  };

  // Load Caps UI script
  useEffect(() => {
    if (window.customElements?.get('caps-ui')) {
      setScriptLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = capsUiUrl;
    script.onload = () => setScriptLoaded(true);
    script.onerror = () => setError('Failed to load Caps UI script');
    document.head.appendChild(script);
  }, []);

  // Create and configure element
  useEffect(() => {
    if (
      !scriptLoaded ||
      (!config.token && !imsLoginRequired) ||
      !containerRef.current
    ) {
      return;
    }

    try {
      if (capsElementRef.current) {
        applyConfigAndAttributes();
        return;
      }

      containerRef.current.innerHTML = '';
      const capsElement = document.createElement('caps-ui');
      capsElementRef.current = capsElement;
      containerRef.current.appendChild(capsElement);

      // Apply configuration after element is attached
      requestAnimationFrame(() => {
        applyConfigAndAttributes();
      });
    } catch (err) {
      setError('Failed to create Caps UI element');
    }
  }, [scriptLoaded, config.token, imsLoginRequired, isInternalUser]);

  // Update element when attributes change
  useEffect(() => {
    if (capsElementRef.current) {
      applyConfigAndAttributes();
    }
  }, [attributes.project, attributes.environment]);


  if (error) {
    return (
      <IllustratedMessage marginTop="size-600">
          <Heading>Error</Heading>
          <Content>{error}</Content>
          <Error />
      </IllustratedMessage>
    );
  }

  return (
    <div className="capsUi">
      {isLoading && <ProgressCircle aria-label="Loading…" isIndeterminate/>}
      {adobeEmail && (
        <div style={{ marginBottom: 'var(--spectrum-global-dimension-size-150, var(--spectrum-alias-size-150))'}}>
          Signed in with Adobe ID: <a href="https://account.adobe.com" target="_blank" rel="noopener noreferrer">{adobeEmail}</a>
        </div>
      )}
      <div style={{ opacity: isLoading ? 0.0 : 1 }} ref={containerRef} />
    </div>
  );
};

export default CapsUi;
